import { useMemo } from "react";
import { useEgdHubData } from "src/hooks/useEgdHubData";
import { isPayloadSource } from "src/utils/hubSource";
import {
  PROGRESSIVE_THRESHOLD,
  getHighestJackpotForGame,
  getJackpotGames,
  getHighestJackpotPerGame,
  buildJackpotByGameId,
} from "src/utils/gameListUtils";

/**
 * @typedef {import('src/api/payload/egd').EgdHubCard} EgdHubCard
 * @typedef {'new-and-popular' | 'progressive-jackpots' | 'cny'} EgdHubViewSlug
 */

const ersCardsForView = (view, gameList, jackpotList) => {
  if (!Array.isArray(gameList) || !Array.isArray(jackpotList)) return [];

  if (view === "progressive-jackpots") {
    return (getJackpotGames(jackpotList, gameList) || []).map((entry) => ({
      game: entry.game,
      jackpot: entry,
    }));
  }

  const statusFilter = view === "cny" ? "CNY" : "new";

  return gameList
    .filter((game) => game.status === statusFilter)
    .map((game) => ({
      game,
      jackpot: getHighestJackpotForGame(game, jackpotList),
    }));
};

/**
 * Builds hub cards from Payload data. `gameList` is already filtered
 * server-side to this view's status (or fetched as `[]` and unused, for
 * progressive-jackpots), so no client-side status filter is needed here.
 * `jackpotList` (depth=2) already carries each meter's resolved `game`, so
 * games are joined by `game.id` — no name/id lookup against a separate list.
 */
const payloadCardsForView = (view, gameList, jackpotList) => {
  if (!Array.isArray(gameList) || !Array.isArray(jackpotList)) return [];

  if (view === "progressive-jackpots") {
    return getHighestJackpotPerGame(jackpotList);
  }

  const jackpotByGameId = buildJackpotByGameId(jackpotList);

  return gameList.map((game) => ({
    game,
    jackpot: jackpotByGameId.get(game.id) || null,
  }));
};

/**
 * Unified EGD hub cards — Payload or legacy ERS, joined client-side either way.
 *
 * @param {EgdHubViewSlug} view
 * @param {'en' | 'fr'} locale
 * @returns {{
 *   cards: EgdHubCard[],
 *   progressiveJackpotThreshold: number,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export function useEgdHubCards(view, locale) {
  const usePayload = isPayloadSource("egd");
  const hubData = useEgdHubData(locale, view);

  const cards = useMemo(() => {
    return usePayload
      ? payloadCardsForView(view, hubData.gameList, hubData.jackpotList)
      : ersCardsForView(view, hubData.gameList, hubData.jackpotList);
  }, [usePayload, view, hubData.gameList, hubData.jackpotList]);

  return {
    cards,
    progressiveJackpotThreshold: PROGRESSIVE_THRESHOLD,
    loading: hubData.loading,
    error: hubData.error,
  };
}
